import isMobile from "ismobilejs";
import store from "../../../../store";

/* Aspect ratios (means can calculate width from height e.g. 720p —> 720p * 16/9 = 1280p) */
const RATIO_4_3 = 4 / 3;
const RATIO_16_9 = 16 / 9;

/** Resolutions
 *
 * 352 x 240 (240p) (SD)
 * 480 x 360 (360p) STANDARD MOBILE
 * 858 x 480 (480p)
 * 1280 x 720 (720p) (Half HD) STANDARD DESKTOP
 * 1920 x 1080 (1080p) (Full HD)
 *
 */
const COFOCUS_DEFAULT_RESO_WIDTH = 720; /* NON-STANDARD (usually 720p is for height, in Cofocus it's for width & height will auto) */
const MOBILE_RESO_HEIGHT = 360;
const MOBILE_RESO_WIDTH = MOBILE_RESO_HEIGHT * RATIO_4_3;

/* Media types corresponding to respective device types */
const mediaTypes = {
  microphone: "audioinput",
  camera: "videoinput",
  speaker: "audiooutput",
};

export async function getMediaOptions(audioOnly = false, videoOnly = false) {
  /* Prepare default options */
  let audioOptions = {};
  let videoOptions = { width: COFOCUS_DEFAULT_RESO_WIDTH };

  /* Handle default resolution for mobile */
  if (isMobile().phone) {
    videoOptions.height = MOBILE_RESO_HEIGHT;
    videoOptions.width = MOBILE_RESO_WIDTH;
  }

  const sessionPreferences = store.state.room.sessionPreferences;

  /* If session preferences not available, connect with Cofocus defaults */
  if (!sessionPreferences) {
    console.log("Session preferences missing.");
    let mediaOptions = {};
    if (audioOnly) {
      mediaOptions = true;
    } else if (videoOnly) {
      mediaOptions = videoOptions;
    } else {
      mediaOptions = { audioOptions: true, videoOptions };
    }
    return mediaOptions;
  }

  /* Specify user's preferred aspect ratio to connect with */
  let aspectRatio;
  if (sessionPreferences.aspectRatio == "16:9") {
    aspectRatio = RATIO_16_9;
  } else if (sessionPreferences.aspectRatio == "4:3") {
    aspectRatio = RATIO_4_3;
  }

  /* Specify resolution to connect user with */
  if (isMobile().phone) {
    videoOptions.width = MOBILE_RESO_WIDTH;
    videoOptions.height = MOBILE_RESO_HEIGHT;
  } else {
    const defaultResolution = sessionPreferences.resolution;
    if (defaultResolution) {
      videoOptions.height = defaultResolution;
      videoOptions.width = defaultResolution * aspectRatio;
    }
  }

  audioOptions.deviceId = await setCurrentDeviceFromDevices("microphone", "audioinput");
  videoOptions.deviceId = await setCurrentDeviceFromDevices("camera", "videoinput");
  await setCurrentDeviceFromDevices("speaker", "audiooutput");

  /* FPS (frames per second) — max 30fps / 24fps / 15fps */
  const defaultFPS = sessionPreferences.fps;
  if (defaultFPS === 30 || defaultFPS === 24 || defaultFPS === 15) {
    videoOptions.frameRate = defaultFPS;
  } else {
    videoOptions.frameRate = 24;
  }

  /* Max Video Bitrate — unlimited / 2000000bps / 500000bps */
  const defaultMaxVideoBitrate = sessionPreferences.maxVideoBitrate;
  if (defaultMaxVideoBitrate === 2000000 || defaultMaxVideoBitrate === 500000) {
    videoOptions.maxVideoBitrate = defaultMaxVideoBitrate;
  }

  console.log("@getMediaOptions", audioOptions, videoOptions);
  let mediaOptions = {};
  if (audioOnly) {
    mediaOptions = audioOptions;
  } else if (videoOnly) {
    mediaOptions = videoOptions;
  } else {
    mediaOptions = { audioOptions, videoOptions };
  }
  return mediaOptions;
}

/**
 *
 * @param {*} deviceType: String — "microphone", "camera", "speaker"
 * @returns: deviceId of the device made as current device for passed deviceType
 */
export async function setCurrentDeviceFromDevices(deviceType) {
  if (!deviceType) return;
  const sessionPreferences = store.state.room.sessionPreferences;
  if (!sessionPreferences) return;
  const preferenceId = sessionPreferences[deviceType];
  const hasDevicePreference = preferenceId && preferenceId !== "default";

  /* Get all available media devices */
  const mediaDevices = await navigator.mediaDevices.enumerateDevices();

  let device = null;

  if (hasDevicePreference) {
    /* Check if preferred device is available */
    device = mediaDevices.find((d) => d.deviceId === preferenceId);
  }

  /* If no preference or preferred device not available, get first one */
  if (!device || device === undefined || device === null) {
    /* Get all available devices based on mediaType */
    const mediaType = mediaTypes[deviceType];
    let devices = [];
    for (var i = 0; i < mediaDevices.length; i++) {
      let mediaDevice = mediaDevices[i];
      if (mediaDevice.kind == mediaType) {
        devices.push(mediaDevice);
      }
    }
    device = devices[0];
  }

  if (!device) {
    return; /* Audio output device often does not exist as available device in use at this stage if no partner */
  }

  store.dispatch("room/setCurrentDevice", device);
  return device.deviceId;
}

export async function setAudioOutputDeviceAsCurrent(deviceId) {
  if (!deviceId) return;
  const mediaDevices = await navigator.mediaDevices.enumerateDevices();
  const speaker = mediaDevices.find((device) => device.deviceId === deviceId);
  if (!speaker) {
    return console.log("Device not found.");
  } else {
    store.dispatch("room/setCurrentDevice", speaker);
  }
}

export async function getCurrentAudioOutputDeviceId() {
  const sessionPreferences = store.state.room.sessionPreferences;
  const defaulSpeaker = sessionPreferences.speaker;
  const hasSpeakerPreference = defaulSpeaker && defaulSpeaker !== "default";

  /* Get all available media devices */
  const devices = await navigator.mediaDevices.enumerateDevices();

  /** Check if device preference exists and set the preferred device if it is available */
  let speaker = hasSpeakerPreference ? devices.find((device) => device.deviceId === defaulSpeaker) : null;

  /* If no preference or preferred device not available, get first one */
  if (!speaker || speaker === undefined || speaker === null) {
    /* Get all available audio outputs (speakers) */
    const speakers = devices.filter((device) => device.kind === "audiooutput");
    speaker = speakers[0];
  }

  /* Check if the speaker exists and return the deviceId, otherwise return null */
  return speaker && speaker.deviceId ? speaker.deviceId : null;
}
